import { resolvePayrollTime } from "./payrollTime";

function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof dateValue === "object" && typeof dateValue.toDate === "function") {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string" || typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  } catch (error) {
    console.error("Fel vid datumtolkning:", error);
    return null;
  }
}

function getMonthIndexFromSwedish(monthName) {
  const months = {
    Januari: 0,
    Februari: 1,
    Mars: 2,
    April: 3,
    Maj: 4,
    Juni: 5,
    Juli: 6,
    Augusti: 7,
    September: 8,
    Oktober: 9,
    November: 10,
    December: 11,
  };

  return months[monthName] ?? 0;
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function normalizeDayName(day) {
  const map = {
    monday: "måndag",
    tuesday: "tisdag",
    wednesday: "onsdag",
    thursday: "torsdag",
    friday: "fredag",
    saturday: "lördag",
    sunday: "söndag",
    måndag: "måndag",
    tisdag: "tisdag",
    onsdag: "onsdag",
    torsdag: "torsdag",
    fredag: "fredag",
    lördag: "lördag",
    söndag: "söndag",
  };

  return map[String(day || "").trim()] || String(day || "").toLowerCase().trim();
}

function getSwedishWeekday(date) {
  const names = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"];
  return names[date.getDay()];
}

function timeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") return 0;

  const [h, m] = timeString.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;

  return h * 60 + m;
}

function calculateTotalShiftMinutes(shift) {
  if (!shift?.startTime || !shift?.endTime) return 0;

  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));

  let totalMinutes = endMinutes - startMinutes;
  if (totalMinutes < 0) totalMinutes += 24 * 60;

  return totalMinutes;
}

function calculateTotalBreakMinutes(employeeData) {
  let totalBreakMinutes = 0;

  if (employeeData?.breaks && Array.isArray(employeeData.breaks)) {
    employeeData.breaks.forEach((breakItem) => {
      if (breakItem.appliesToWholeShift) return;

      if (breakItem.startTime && breakItem.endTime) {
        const start = timeToMinutes(breakItem.startTime);
        const end = timeToMinutes(breakItem.endTime);

        let breakMinutes = end - start;
        if (breakMinutes < 0) breakMinutes += 24 * 60;

        totalBreakMinutes += breakMinutes;
      }
    });
  }

  return totalBreakMinutes;
}

function calculateShiftHours(shift, employeeData = null) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!shift?.startTime || !shift?.endTime) return 0;

  try {
    const totalMinutes = calculateTotalShiftMinutes(shift);
    let breakMinutes = 0;

    if (employeeData) {
      breakMinutes = calculateTotalBreakMinutes(employeeData);

      if (employeeData.breaks && Array.isArray(employeeData.breaks)) {
        employeeData.breaks.forEach((breakItem) => {
          if (breakItem.appliesToWholeShift) {
            const totalHours = totalMinutes / 60;

            if (totalHours >= 5 && totalHours < 8) {
              breakMinutes = Math.max(breakMinutes, 30);
            } else if (totalHours >= 8) {
              breakMinutes = Math.max(breakMinutes, 60);
            }
          }
        });
      }
    } else {
      breakMinutes = Number(shift.breakMinutes || shift.break || 0);
    }

    let workedMinutes = Math.max(0, totalMinutes - breakMinutes);

    if (employeeData?.absences && Array.isArray(employeeData.absences)) {
      employeeData.absences.forEach((absenceItem) => {
        let absenceMinutes = 0;

        if (absenceItem.appliesToWholeShift) {
          absenceMinutes = workedMinutes;
        } else if (absenceItem.startTime && absenceItem.endTime) {
          const start = timeToMinutes(absenceItem.startTime);
          const end = timeToMinutes(absenceItem.endTime);

          let minutes = end - start;
          if (minutes < 0) minutes += 24 * 60;

          absenceMinutes = minutes;
        } else if (Number(absenceItem.hours || 0) > 0) {
          absenceMinutes = Number(absenceItem.hours || 0) * 60;
        }

        workedMinutes = Math.max(0, workedMinutes - absenceMinutes);
      });
    }

    return workedMinutes / 60;
  } catch (error) {
    console.error("Fel vid beräkning av passets timmar:", error);
    return 0;
  }
}

function calculateOvertimeForShift(shift, employeeData = null) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!shift?.startTime || !shift?.endTime) {
    return {
      hours: 0,
      premiumValue: 0,
      regularHours: 0,
      premiumHours: 0,
      hoursByCode: {},
    };
  }

  try {
    let totalOvertimeMinutes = 0;
    let premiumOvertimeMinutes = 0;
    let premiumValue = 0;
    const minutesByCode = {};

    if (employeeData?.overtime && Array.isArray(employeeData.overtime)) {
      employeeData.overtime.forEach((overtimeItem) => {
        let itemMinutes = 0;

        if (overtimeItem.appliesToWholeShift) {
          const totalMinutes = calculateTotalShiftMinutes(shift);
          const breakMinutes = calculateTotalBreakMinutes(employeeData);
          itemMinutes = Math.max(0, totalMinutes - breakMinutes);
        } else if (overtimeItem.startTime && overtimeItem.endTime) {
          const start = timeToMinutes(overtimeItem.startTime);
          const end = timeToMinutes(overtimeItem.endTime);

          let minutes = end - start;
          if (minutes < 0) {
            minutes += 24 * 60;
          }
          itemMinutes = minutes;
        } else if (Number(overtimeItem.hours || 0) > 0) {
          itemMinutes = Number(overtimeItem.hours || 0) * 60;
        }

        const percentage = Number(overtimeItem.percentage || 0);
        const requestedCode = String(
          overtimeItem.paxmlCode ||
            overtimeItem.payrollCode ||
            overtimeItem.timeCode ||
            overtimeItem.code ||
            ""
        ).trim().toUpperCase();
        const code = /^ÖB[1-5]$/.test(requestedCode) ? requestedCode : "ÖB1";
        totalOvertimeMinutes += itemMinutes;
        minutesByCode[code] = (minutesByCode[code] || 0) + itemMinutes;

        if (percentage > 0) {
          premiumOvertimeMinutes += itemMinutes;
          premiumValue += (itemMinutes / 60) * (percentage / 100);
        }
      });
    }

    return {
      hours: totalOvertimeMinutes / 60,
      premiumValue,
      regularHours: (totalOvertimeMinutes - premiumOvertimeMinutes) / 60,
      premiumHours: premiumOvertimeMinutes / 60,
      hoursByCode: Object.fromEntries(
        Object.entries(minutesByCode).map(([code, minutes]) => [code, minutes / 60])
      ),
    };
  } catch (error) {
    console.error("Fel vid beräkning av övertid/mertid:", error);
    return { hours: 0, premiumValue: 0, regularHours: 0, premiumHours: 0, hoursByCode: {} };
  }
}

function calculateAbsenceForShift(shift, employeeData = null) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!shift?.startTime || !shift?.endTime) return { hours: 0, value: 0 };

  try {
    let absenceMinutes = 0;
    let absenceValue = 0;

    if (employeeData?.absences && Array.isArray(employeeData.absences)) {
      employeeData.absences.forEach((absenceItem) => {
        let itemMinutes = 0;

        if (absenceItem.appliesToWholeShift) {
          const totalMinutes = calculateTotalShiftMinutes(shift);
          const breakMinutes = calculateTotalBreakMinutes(employeeData);
          itemMinutes = Math.max(0, totalMinutes - breakMinutes);
        } else if (absenceItem.startTime && absenceItem.endTime) {
          const start = timeToMinutes(absenceItem.startTime);
          const end = timeToMinutes(absenceItem.endTime);

          let minutes = end - start;
          if (minutes < 0) minutes += 24 * 60;

          itemMinutes = minutes;
        } else if (Number(absenceItem.hours || 0) > 0) {
          itemMinutes = Number(absenceItem.hours || 0) * 60;
        }

        absenceMinutes += itemMinutes;

        const salaryPercent = Number(absenceItem.absencePercent ?? absenceItem.salaryPercent ?? 0);
        const deductionPercent = (100 - salaryPercent) / 100;
        absenceValue += (itemMinutes / 60) * deductionPercent;
      });
    }

    return {
      hours: absenceMinutes / 60,
      value: absenceValue,
    };
  } catch (error) {
    console.error("Fel vid beräkning av frånvaro:", error);
    return { hours: 0, value: 0 };
  }
}

function calculateEntryHours(shift, entry, employeeData) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!entry) return 0;

  if (entry.appliesToWholeShift) {
    return Math.max(
      0,
      calculateTotalShiftMinutes(shift) - calculateTotalBreakMinutes(employeeData)
    ) / 60;
  }

  if (entry.startTime && entry.endTime) {
    let minutes = timeToMinutes(entry.endTime) - timeToMinutes(entry.startTime);
    if (minutes < 0) minutes += 24 * 60;
    return minutes / 60;
  }

  const hours = Number(entry.hours || 0);
  return Number.isFinite(hours) && hours > 0 ? hours : 0;
}

function getFortnoxAbsenceCode(absence) {
  const configuredCode = String(
    absence?.paxmlCode ||
      absence?.payrollCode ||
      absence?.timeCode ||
      absence?.code ||
      ""
  ).trim().toUpperCase();

  if (/^(SJK|SJK_KAR|SJK_LÖN|SJK_ERS|SJK_PEN|ASK|HAV|FPE|VAB|SMB|UTB|MIL|SVE|NÄR|TJL|SEM|SEM_BET|SEM_SPAR|SEM_OBE|SEM_FÖR|KOM|PEM|PER|FAC|ATK|KON|PAP|JS[1-3]|BS[1-3]|ATF|FR[1-9])$/.test(configuredCode)) {
    return configuredCode;
  }

  const name = String(
    absence?.absenceTypeName || absence?.type || absence?.name || ""
  ).toLocaleLowerCase("sv-SE");

  if (name.includes("vab") || name.includes("vård av barn")) return "VAB";
  if (name.includes("sjuk")) return "SJK";
  if (name.includes("föräld")) return "FPE";
  if (name.includes("semester")) return "SEM";
  if (name.includes("tjänstledig")) return "TJL";
  if (name.includes("komp")) return "KOM";
  if (name.includes("permission")) return "PEM";
  return "FR1";
}

function getShiftSegments(shift) {
  const shiftDate = safeParseDate(shift?.date);
  if (!shiftDate || !shift?.startTime || !shift?.endTime) return [];

  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));

  if (startMinutes === endMinutes) return [];

  if (endMinutes > startMinutes) {
    return [
      {
        date: new Date(shiftDate),
        dateKey: formatDateKey(shiftDate),
        startMinute: startMinutes,
        endMinute: endMinutes,
      },
    ];
  }

  const nextDate = addDays(shiftDate, 1);

  return [
    {
      date: new Date(shiftDate),
      dateKey: formatDateKey(shiftDate),
      startMinute: startMinutes,
      endMinute: 1440,
    },
    {
      date: new Date(nextDate),
      dateKey: formatDateKey(nextDate),
      startMinute: 0,
      endMinute: endMinutes,
    },
  ];
}

function buildAbsenceIntervals(segments, employeeData) {
  const absenceIntervals = [];

  if (employeeData?.absences && Array.isArray(employeeData.absences)) {
    employeeData.absences.forEach((absenceItem) => {
      if (absenceItem.appliesToWholeShift) {
        segments.forEach((segment) => {
          absenceIntervals.push({
            start: segment.startMinute,
            end: segment.endMinute,
          });
        });
      } else if (absenceItem.startTime && absenceItem.endTime) {
        const start = timeToMinutes(absenceItem.startTime);
        const end = timeToMinutes(absenceItem.endTime);

        if (end < start) {
          segments.forEach((segment) => {
            if (segment.startMinute <= start && segment.endMinute >= 1440) {
              absenceIntervals.push({ start, end: 1440 });
            }
            if (segment.startMinute <= end && segment.endMinute >= 0) {
              absenceIntervals.push({ start: 0, end });
            }
          });
        } else {
          segments.forEach((segment) => {
            const overlapStart = Math.max(segment.startMinute, start);
            const overlapEnd = Math.min(segment.endMinute, end);
            if (overlapEnd > overlapStart) {
              absenceIntervals.push({
                start: overlapStart,
                end: overlapEnd,
              });
            }
          });
        }
      }
    });
  }

  return absenceIntervals;
}

// ============================================================
// NYA OB-HELPERS (samma som i TimeReportPopup)
// ============================================================

function getDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const parsed = safeParseDate(value);
  return parsed ? formatDateKey(parsed) : "";
}

function sourceAppliesToMinute({
  dateKey,
  previousDateKey,
  minute,
  start,
  end,
  sourceDateKey,
}) {
  if (start <= end) {
    return sourceDateKey === dateKey && minute >= start && minute < end;
  }
  return (
    (sourceDateKey === dateKey && minute >= start && minute < 1440) ||
    (sourceDateKey === previousDateKey && minute >= 0 && minute < end)
  );
}

function getRuleDays(rule) {
  const rawDays =
    rule?.days ||
    rule?.selectedDays ||
    rule?.weekdays ||
    rule?.selectedWeekdays ||
    [];
  return Array.isArray(rawDays) ? rawDays.map(normalizeDayName) : [];
}

function getActiveObSourcesAtMinute(minute, date, obRules, holidays, customObEntries = []) {
  const sources = [];
  const dateKey = formatDateKey(date);
  const previousDate = addDays(date, -1);
  const previousDateKey = formatDateKey(previousDate);
  const currentWeekday = getSwedishWeekday(date);
  const previousWeekday = getSwedishWeekday(previousDate);

  (obRules || []).forEach((rule) => {
    const name = rule?.name || rule?.title || "OB";

    if (rule?.useSpecificDates) {
      const specificDates = Array.isArray(rule?.specificDates)
        ? rule.specificDates
        : [];

      specificDates.forEach((item) => {
        const amount = Number(
          item?.amount ??
          rule?.amount ??
          rule?.obAmount ??
          rule?.value ??
          0
        );
        if (amount <= 0) return;

        const sourceDateKey = getDateKey(item?.date);
        const start = timeToMinutes(
          item?.startTime || rule?.startTime || rule?.from || "00:00"
        );
        const end = timeToMinutes(
          item?.endTime || rule?.endTime || rule?.to || "23:59"
        );

        if (
          sourceAppliesToMinute({
            dateKey,
            previousDateKey,
            minute,
            start,
            end,
            sourceDateKey,
          })
        ) {
          sources.push({
            name: item?.name || name,
            amount,
            type: "obRule",
            code: item?.paxmlCode || rule?.paxmlCode || rule?.payrollCode || rule?.timeCode || rule?.code,
          });
        }
      });
      return;
    }

    const amount = Number(
      rule?.amount ??
      rule?.obAmount ??
      rule?.value ??
      0
    );
    if (amount <= 0) return;

    const start = timeToMinutes(rule?.startTime || rule?.from || "00:00");
    const end = timeToMinutes(rule?.endTime || rule?.to || "23:59");
    const days = getRuleDays(rule);

    const appliesToday = days.length === 0 || days.includes(currentWeekday);
    const appliesPreviousDay = days.length === 0 || days.includes(previousWeekday);

    if (start <= end) {
      if (appliesToday && minute >= start && minute < end) {
        sources.push({
          name,
          amount,
          type: "obRule",
          code: rule?.paxmlCode || rule?.payrollCode || rule?.timeCode || rule?.code,
        });
      }
    } else {
      if (appliesToday && minute >= start && minute < 1440) {
        sources.push({
          name,
          amount,
          type: "obRule",
          code: rule?.paxmlCode || rule?.payrollCode || rule?.timeCode || rule?.code,
        });
      }
      if (appliesPreviousDay && minute >= 0 && minute < end) {
        sources.push({
          name,
          amount,
          type: "obRule",
          code: rule?.paxmlCode || rule?.payrollCode || rule?.timeCode || rule?.code,
        });
      }
    }
  });

  (holidays || []).forEach((holiday) => {
    const amount = Number(holiday?.amount || 0);
    if (amount <= 0) return;

    const holidayDateKey = getDateKey(holiday?.date);
    const start = timeToMinutes(holiday?.startTime || "00:00");
    const end = timeToMinutes(holiday?.endTime || "23:59");

    if (
      sourceAppliesToMinute({
        dateKey,
        previousDateKey,
        minute,
        start,
        end,
        sourceDateKey: holidayDateKey,
      })
    ) {
      sources.push({
        name: holiday?.name || "Helgdag",
        amount,
        type: "holiday",
        code: holiday?.paxmlCode || holiday?.payrollCode || holiday?.timeCode || "HLG",
      });
    }
  });

  (customObEntries || []).forEach((customOb) => {
    const amount = Number(customOb?.amount || 0);
    if (amount <= 0) return;

    const name = customOb?.name || customOb?.description || "Eget OB-tillägg";
    const customDateKey = getDateKey(customOb?.date);
    const start = timeToMinutes(customOb?.startTime || "00:00");
    const end = timeToMinutes(customOb?.endTime || "23:59");

    if (
      sourceAppliesToMinute({
        dateKey,
        previousDateKey,
        minute,
        start,
        end,
        sourceDateKey: customDateKey,
      })
    ) {
      sources.push({
        name,
        amount,
        type: "custom",
        customObId: customOb.id,
        code: customOb?.paxmlCode || customOb?.payrollCode || customOb?.timeCode || customOb?.code,
      });
    }
  });

  return sources;
}

// UPPDATERAD: calculateObForShift - räknar varje OB-källa separat
function calculateObForShift(shift, obRules, holidays, obEnabled = true, employeeData = null, customObEntries = []) {
  ({ shift, employeeData } = resolvePayrollTime(shift, employeeData));
  if (!obEnabled) return { hours: 0, value: 0, hoursByCode: {}, hoursByDateAndCode: {} };

  const segments = getShiftSegments(shift);
  if (segments.length === 0) return { hours: 0, value: 0, hoursByCode: {}, hoursByDateAndCode: {} };

  const absenceIntervals = buildAbsenceIntervals(segments, employeeData);

  const isMinuteAbsent = (minute) => {
    return absenceIntervals.some(
      (interval) => minute >= interval.start && minute < interval.end
    );
  };

  let obMinutes = 0;
  let obValue = 0;
  const hoursByCode = {};
  const valueByCode = {};
  const hoursByDateAndCode = {};

  segments.forEach((segment) => {
    for (let minute = segment.startMinute; minute < segment.endMinute; minute++) {
      if (isMinuteAbsent(minute)) continue;

      const activeSources = getActiveObSourcesAtMinute(
        minute,
        segment.date,
        obRules,
        holidays,
        customObEntries
      ).filter((source) => Number(source.amount || 0) > 0);

      // Varje aktiv OB-källa räknas separat
      activeSources.forEach((source) => {
        const requestedCode = String(source.code || "").trim().toUpperCase();
        const code = /^(OB[1-5]|OS[1-5]|HLG|SKI|LT[1-9])$/.test(requestedCode)
          ? requestedCode
          : "OB1";
        obMinutes += 1;
        obValue += Number(source.amount || 0) / 60;
        hoursByCode[code] = (hoursByCode[code] || 0) + 1 / 60;
        valueByCode[code] = (valueByCode[code] || 0) + Number(source.amount || 0) / 60;
        hoursByDateAndCode[segment.dateKey] = hoursByDateAndCode[segment.dateKey] || {};
        hoursByDateAndCode[segment.dateKey][code] =
          (hoursByDateAndCode[segment.dateKey][code] || 0) + 1 / 60;
      });
    }
  });

  return {
    hours: obMinutes / 60,
    value: obValue,
    hoursByCode,
    valueByCode,
    hoursByDateAndCode,
  };
}

// ============================================================
// SLUT PÅ NYA OB-HELPERS
// ============================================================

function getEmployeeName(employee) {
  if (!employee) return "Okänd anställd";
  if (employee.name) return employee.name;
  if (employee.firstName && employee.lastName) return `${employee.firstName} ${employee.lastName}`;
  if (employee.firstName) return employee.firstName;
  if (employee.lastName) return employee.lastName;
  if (employee.displayName) return employee.displayName;
  if (employee.userName) return employee.userName;
  return "Okänd anställd";
}

function getEmployeeIdFromRequest(request, employees = []) {
  if (!request) return null;

  if (request.employeeId) return request.employeeId;
  if (request.userId) return request.userId;
  if (request.uid) return request.uid;

  const email = String(request.userEmail || request.email || "")
    .trim()
    .toLowerCase();

  if (!email) return null;

  const employee = employees.find(
    (emp) => String(emp.email || "").trim().toLowerCase() === email
  );

  return employee?.id || null;
}

function getRequestDate(request) {
  return (
    safeParseDate(request.date) ||
    safeParseDate(request.shiftDate) ||
    safeParseDate(request.submittedAt) ||
    safeParseDate(request.createdAt)
  );
}


function getRequestShiftId(request) {
  return (
    request?.shiftId ||
    request?.originalShiftId ||
    request?.scheduleShiftId ||
    request?.passId ||
    request?.workShiftId ||
    null
  );
}

function getRequestHours(request) {
  const value =
    request?.hours ??
    request?.overtimeHours ??
    request?.requestedHours ??
    request?.durationHours ??
    request?.totalHours ??
    0;

  const hours = Number(value || 0);
  return Number.isFinite(hours) && hours > 0 ? hours : 0;
}

function findMatchingOvertimeRule(request, overtimeRules = []) {
  const ruleId =
    request?.overtimeRuleId ||
    request?.ruleId ||
    request?.overtimeTypeId ||
    request?.selectedOvertimeRuleId ||
    request?.selectedRuleId ||
    null;

  if (ruleId) {
    const byId = overtimeRules.find((rule) => String(rule?.id) === String(ruleId));
    if (byId) return byId;
  }

  const requestedName = String(
    request?.overtimeRuleName ||
      request?.ruleName ||
      request?.overtimeTypeName ||
      request?.typeName ||
      request?.type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (requestedName) {
    const byName = overtimeRules.find((rule) =>
      [rule?.name, rule?.title, rule?.type]
        .filter(Boolean)
        .some((value) => String(value).trim().toLowerCase() === requestedName)
    );
    if (byName) return byName;
  }

  return null;
}

function getApprovedOvertimePercentage(
  request,
  matchingRule = null,
  overtimeRules = []
) {
  const fallbackRule =
    matchingRule ||
    overtimeRules.find((rule) => Number(rule?.percentage || 0) > 0) ||
    null;

  const candidates = [
    request?.percentage,
    request?.overtimePercentage,
    request?.percent,
    request?.ratePercent,
    request?.premiumPercentage,
    request?.supplementPercentage,
    request?.selectedOvertimeRule?.percentage,
    request?.overtimeRule?.percentage,
    request?.overtime?.percentage,
    fallbackRule?.percentage,
    fallbackRule?.percent,
    fallbackRule?.rate,
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === "") continue;
    const percentage = Number(candidate);
    if (Number.isFinite(percentage) && percentage > 0) return percentage;
  }

  return 100;
}

function requestMatchesShift(request, shift) {
  const shiftId = shift?.originalId || shift?.id;
  const requestShiftId = getRequestShiftId(request);

  if (requestShiftId && shiftId) {
    return String(requestShiftId) === String(shiftId);
  }

  const requestDate = getRequestDate(request);
  const shiftDate = safeParseDate(shift?.date);
  if (!requestDate || !shiftDate) return false;

  return formatDateKey(requestDate) === formatDateKey(shiftDate);
}

function buildApprovedOvertimeEntry(request, overtimeRules = []) {
  const matchingRule = findMatchingOvertimeRule(request, overtimeRules);

  return {
    overtimeRequestId: request.id,
    startTime:
      request.startTime || request.overtimeStartTime || request.fromTime || "",
    endTime:
      request.endTime || request.overtimeEndTime || request.toTime || "",
    appliesToWholeShift: Boolean(
      request.appliesToWholeShift ?? request.wholeShift ?? request.isWholeShift
    ),
    overtimeRuleId:
      request.overtimeRuleId ||
      request.ruleId ||
      request.overtimeTypeId ||
      matchingRule?.id ||
      "approved-request",
    overtimeRuleName:
      request.overtimeRuleName ||
      request.ruleName ||
      request.overtimeTypeName ||
      matchingRule?.name ||
      "Godkänd övertid",
    percentage: getApprovedOvertimePercentage(
      request,
      matchingRule,
      overtimeRules
    ),
    paxmlCode:
      request.paxmlCode ||
      request.payrollCode ||
      request.timeCode ||
      matchingRule?.paxmlCode ||
      matchingRule?.payrollCode ||
      matchingRule?.timeCode ||
      matchingRule?.code ||
      "ÖB1",
    hours: getRequestHours(request),
    approvedFromRequest: true,
  };
}

function mergeApprovedOvertimeForEmployee({
  shift,
  employeeId,
  employeeData,
  approvedOvertimeRequests,
  employees,
  overtimeRules,
}) {
  if (!shift || !employeeId) return employeeData;

  const currentOvertime = Array.isArray(employeeData?.overtime)
    ? employeeData.overtime.filter((entry) => !entry?.approvedFromRequest)
    : [];

  const matchingRequests = (approvedOvertimeRequests || []).filter((request) => {
    if (!requestMatchesShift(request, shift)) return false;

    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    return String(requestEmployeeId || "") === String(employeeId);
  });

  const approvedEntries = matchingRequests.map((request) =>
    buildApprovedOvertimeEntry(request, overtimeRules)
  );

  return {
    ...employeeData,
    overtime: [...currentOvertime, ...approvedEntries],
  };
}

function buildApprovedAbsenceEntry(request, absenceTypes = []) {
  const matchingType = request?.absenceTypeId
    ? absenceTypes.find((type) => type.id === request.absenceTypeId)
    : null;

  return {
    absenceRequestId: request.id,
    startTime: request.startTime || "",
    endTime: request.endTime || "",
    appliesToWholeShift: Boolean(request.appliesToWholeShift),
    absenceTypeId: request.absenceTypeId || matchingType?.id || "approved-request",
    absenceTypeName:
      request.absenceTypeName || matchingType?.name || "Godkänd frånvaro",
    type: request.absenceTypeName || matchingType?.name || "Godkänd frånvaro",
    absencePercent: Number(
      request.absencePercent ??
        request.salaryPercent ??
        matchingType?.salaryPercent ??
        0
    ),
    salaryPercent: Number(
      request.absencePercent ??
        request.salaryPercent ??
        matchingType?.salaryPercent ??
        0
    ),
    hours: Number(request.hours || 0),
    approvedFromRequest: true,
  };
}

function mergeApprovedAbsenceForEmployee({
  shift,
  employeeId,
  employeeData,
  approvedAbsenceRequests,
  employees,
  absenceTypes,
}) {
  const shiftId = shift?.originalId || shift?.id;
  if (!shiftId || !employeeId) return employeeData;

  const currentAbsences = Array.isArray(employeeData?.absences)
    ? employeeData.absences.filter((entry) => !entry?.approvedFromRequest)
    : [];

  const matchingRequests = (approvedAbsenceRequests || []).filter((request) => {
    const requestShiftId = request?.shiftId || request?.originalShiftId;
    if (requestShiftId !== shiftId) return false;

    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    return requestEmployeeId === employeeId;
  });

  const requestAbsenceEntries = matchingRequests
    .map((request) => buildApprovedAbsenceEntry(request, absenceTypes));

  if (requestAbsenceEntries.length === 0) return employeeData;

  return {
    ...employeeData,
    absences: [...currentAbsences, ...requestAbsenceEntries],
  };
}

function buildEmployeeEntriesFromShift(shift) {
  const entries = [];
  const addedEmployeeIds = new Set();

  const addEntry = (employeeId, employeeData = {}) => {
    const normalizedEmployeeId = employeeId || null;
    const isObemannad = Boolean(employeeData?.isObemannad);

    if (!normalizedEmployeeId || isObemannad) return;
    if (addedEmployeeIds.has(normalizedEmployeeId)) return;

    addedEmployeeIds.add(normalizedEmployeeId);

    entries.push({
      employeeId: normalizedEmployeeId,
      employeeData: {
        breaks: employeeData?.breaks || [],
        overtime: employeeData?.overtime || [],
        absences: employeeData?.absences || [],
        ...employeeData,
      },
    });
  };

  if (Array.isArray(shift?.employees) && shift.employees.length > 0) {
    shift.employees.forEach((employeeData) => {
      addEntry(employeeData?.employeeId, employeeData);
    });
  } else {
    addEntry(shift?.employeeId, {
      payrollTime: shift?.payrollTime || null,
      breaks: shift?.breaks || [],
      overtime: shift?.overtime || [],
      absences: shift?.absences || [],
      employeeId: shift?.employeeId || null,
      isObemannad: shift?.isObemannad || false,
      name: shift?.name || shift?.employee || shift?.userName || "",
    });
  }

  return entries;
}

export { safeParseDate, getMonthIndexFromSwedish, pad, formatDateKey, calculateShiftHours, calculateOvertimeForShift, calculateAbsenceForShift, calculateEntryHours, getFortnoxAbsenceCode, calculateObForShift, mergeApprovedOvertimeForEmployee, mergeApprovedAbsenceForEmployee, buildEmployeeEntriesFromShift };
